import React, { useState } from 'react';
import GenericModal from '../../../components/modal/GenericModal';
import AsyncSelectInput from '../../../components/Select/AsyncSelectInput';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import {
    designarSuplenciaAPI,
    revocarSuplenciaAPI,
    getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// "Reasignar responsable" de UNA postulación en SU etapa actual (suplencia por
// postulación y etapa, TP_Suplencia). GDH designa a un suplente cuando el responsable
// de la etapa no está disponible. Dos responsables: el Director de la sede en Feedback Dirección
// y el evaluador asignado en Clase Modelo.
//
// Suplente del Director: entra por Gestión de Traslados y Promociones → Ver postulantes y ve/actúa
// SOLO sobre las postulaciones que se le asignaron. Suplente del evaluador de Clase Modelo: la
// ve en "Clases modelo por completar" (Mis traslados y promociones). En ambos casos solo mientras
// dure la indisponibilidad (fechas) y la etapa siga siendo la suya.
//
// POST TrasladosPromociones/DesignarSuplencia | RevocarSuplencia. Las reglas las valida el
// lambda; el ícono solo se ofrece cuando la bandeja trae row.rolResponsableEtapa.
// ──────────────────────────────────────────────────────────────────────────
const ROL_DIRECTOR_COLEGIO = 69;
// Código del responsable de Clase Modelo (no es un IdRol real): el evaluador asignado. Mismo valor
// que GlobalConstants.RESPONSABLE_EVALUADOR_CLASE_MODELO en el lambda (intranet-puestos-backend).
const RESPONSABLE_EVALUADOR_CLASE_MODELO = 905;

const NOMBRE_RESPONSABLE_POR_ROL = {
    [ROL_DIRECTOR_COLEGIO]: 'Director de la sede',
    [RESPONSABLE_EVALUADOR_CLASE_MODELO]: 'Evaluador de clase modelo',
};

const hoyIso = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

// "yyyy-MM-dd" -> "dd/MM/yyyy" sin pasar por Date (evita el corrimiento por zona horaria).
const formatearFechaIso = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).split('-');
    return y && m && d ? `${d}/${m}/${y}` : iso;
};

const ReasignarResponsableEtapaModal = ({ toggleModal, refreshPage, postulante }) => {
    const [managePostRequest] = useManagePostRequest();
    const [suplente, setSuplente] = useState(null);
    const [fechaDesde, setFechaDesde] = useState(hoyIso());
    const [fechaHasta, setFechaHasta] = useState('');
    const [motivo, setMotivo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const responsableActual = NOMBRE_RESPONSABLE_POR_ROL[postulante.rolResponsableEtapa] ?? 'Responsable de la etapa';
    const esClaseModelo = postulante.rolResponsableEtapa === RESPONSABLE_EVALUADOR_CLASE_MODELO;
    const tieneSuplente = !!postulante.idSuplencia;

    const cerrarYRefrescar = () => {
        setIsSubmitting(false);
        toggleModal();
        refreshPage && refreshPage();
    };

    const handleDesignar = () => {
        if (isSubmitting) return;
        if (!suplente?.value) {
            setError('Selecciona al suplente.');
            return;
        }
        if (!fechaDesde) {
            setError('Indica desde cuándo está indisponible el responsable.');
            return;
        }
        if (fechaHasta && fechaHasta < fechaDesde) {
            setError('La fecha hasta no puede ser anterior a la fecha desde.');
            return;
        }
        setError('');
        setIsSubmitting(true);
        managePostRequest(
            designarSuplenciaAPI(),
            {
                IdPostulante: postulante.idPostulante,
                IdEmpleadoSuplente: Number(suplente.value),
                FechaDesde: fechaDesde,
                FechaHasta: fechaHasta || null,
                Motivo: motivo.trim() || null,
            },
            cerrarYRefrescar,
            true,
            () => setIsSubmitting(false),
            true
        );
    };

    const handleRevocar = () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        managePostRequest(
            revocarSuplenciaAPI(),
            { IdSuplencia: postulante.idSuplencia },
            cerrarYRefrescar,
            true,
            () => setIsSubmitting(false),
            true
        );
    };

    return (
        <GenericModal showModal={toggleModal} titulo="Reasignar responsable" width="650px">
            <div className="modal-body">
                <div className="form-group">
                    <label className="modal-label mb-0">Postulante</label>
                    <input type="text" className="form-control" value={postulante.nombrePostulante ?? ''} disabled />
                </div>
                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label className="modal-label mb-0">Etapa</label>
                        <input type="text" className="form-control" value={postulante.desEtapa ?? ''} disabled />
                    </div>
                    <div className="col-sm-6 form-group">
                        <label className="modal-label mb-0">Responsable actual</label>
                        <input type="text" className="form-control" value={responsableActual} disabled />
                    </div>
                </div>

                {tieneSuplente && (
                    <div className="alert alert-info">
                        <strong>Suplente vigente:</strong> {postulante.suplenteNombre}
                        {postulante.suplenteDesde && (
                            <> — desde {formatearFechaIso(postulante.suplenteDesde)}
                                {postulante.suplenteHasta ? ` hasta ${formatearFechaIso(postulante.suplenteHasta)}` : ' (sin fecha de fin)'}
                            </>
                        )}
                        <div className="mt-2">
                            <button type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={isSubmitting}
                                    onClick={handleRevocar}>
                                Quitar suplente
                            </button>
                        </div>
                    </div>
                )}

                <hr />
                <label className="modal-label" style={{ fontWeight: 'bold' }}>
                    {tieneSuplente ? 'Cambiar suplente' : 'Designar suplente'}
                </label>

                <div className="form-group">
                    <label className="modal-label mb-0 required">Suplente</label>
                    <AsyncSelectInput
                        api={getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI}
                        handleSelectInputChange={setSuplente}
                        defaultValue={suplente}
                        placeholder="Buscar suplente..."
                    />
                </div>
                <div className="row">
                    <div className="col-sm-6 form-group">
                        <label className="modal-label mb-0 required">No disponible desde</label>
                        <input type="date" className="form-control" value={fechaDesde}
                               onChange={(e) => setFechaDesde(e.target.value)} />
                    </div>
                    <div className="col-sm-6 form-group">
                        <label className="modal-label mb-0">Hasta (opcional)</label>
                        <input type="date" className="form-control" value={fechaHasta} min={fechaDesde}
                               onChange={(e) => setFechaHasta(e.target.value)} />
                    </div>
                </div>
                <div className="form-group">
                    <label className="modal-label mb-0">Motivo (opcional)</label>
                    <input type="text" className="form-control" maxLength={250} value={motivo}
                           placeholder="Ej.: vacaciones, licencia"
                           onChange={(e) => setMotivo(e.target.value)} />
                </div>
                {esClaseModelo ? (
                    <small className="text-muted d-block">
                        El suplente verá esta clase modelo en "Clases modelo por completar" (Mis traslados y
                        promociones) y podrá enviar la evaluación, mientras esté dentro de las fechas y la
                        clase siga pendiente. El evaluador asignado también puede hacerlo: quien envíe primero
                        cierra el paso. Recibirá un correo.
                    </small>
                ) : (
                    <small className="text-muted d-block">
                        El suplente verá únicamente esta postulación en Gestión de Traslados y Promociones, mientras
                        esté dentro de las fechas y la etapa siga pendiente del responsable. Recibirá un correo y
                        deberá volver a iniciar sesión si es la primera vez que se le asigna una suplencia.
                    </small>
                )}
                {error && <div className="text-danger mt-2">{error}</div>}

                <div className="modal-footer pl-0">
                    <div />
                    <button
                        type="button"
                        className="generic-button-buscador generic-button-hover-verde green-innova-background"
                        onClick={handleDesignar}
                        disabled={isSubmitting}>
                        Guardar
                    </button>
                </div>
            </div>
        </GenericModal>
    )
}

export default ReasignarResponsableEtapaModal
